import {HStack, IconButton, Input, Text, Tooltip} from '@chakra-ui/react';
import {
  ArrowBackIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  EditIcon,
} from '@chakra-ui/icons';
import {useTranslation} from 'react-i18next';

interface ConversationClientHeaderProps {
  titre: string;
  onRetour: () => void;
  onFermer: () => void;
  // Édition du titre (optionnelle)
  edition?: {
    isEditing: boolean;
    valeur: string;
    isModifying: boolean;
    onChange: (valeur: string) => void;
    onStart: (valeurInitiale: string) => void;
    onSubmit: () => void;
    onCancel: () => void;
  };
}

/**
 * Header réutilisable pour les vues de conversation côté client.
 * Affiche un bouton retour, le titre, et un bouton fermer.
 * En fiche client / mode commercial, le titre devient éditable via la prop `edition`.
 */
export const ConversationClientHeader: React.FC<
  ConversationClientHeaderProps
> = ({titre, onRetour, onFermer, edition}) => {
  const {t} = useTranslation();

  const handleTitreKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!edition) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      edition.onSubmit();
    } else if (e.key === 'Escape') {
      edition.onCancel();
    }
  };

  return (
    <HStack
      as='header'
      justify='space-between'
      align='center'
      borderBottom='1px solid'
      borderColor='gray.light'
      py={3}
      px={4}
    >
      <HStack
        spacing={2}
        flex={1}
        minW={0}
      >
        <IconButton
          aria-label={t('client.conversations.nouvelle.retour')}
          icon={<ArrowBackIcon boxSize={5} />}
          variant='ghost'
          color='gray.dark'
          size='sm'
          onClick={onRetour}
        />
        {edition?.isEditing ? (
          <HStack
            spacing={1}
            flex={1}
            minW={0}
          >
            <Input
              value={edition.valeur}
              onChange={(e) => {
                edition.onChange(e.target.value);
              }}
              onKeyDown={handleTitreKeyDown}
              size='sm'
              fontWeight='700'
              autoFocus
              isDisabled={edition.isModifying}
            />
            <IconButton
              aria-label={t('global.valider')}
              icon={<CheckIcon />}
              variant='ghost'
              color='gray.dark'
              size='sm'
              onClick={edition.onSubmit}
              isLoading={edition.isModifying}
              isDisabled={!edition.valeur.trim()}
            />
            <IconButton
              aria-label={t('global.annuler')}
              icon={<CloseIcon />}
              variant='ghost'
              color='gray.dark'
              size='sm'
              onClick={edition.onCancel}
              isDisabled={edition.isModifying}
            />
          </HStack>
        ) : (
          <>
            <Text
              fontSize='md'
              fontWeight='700'
              color='black.text'
              noOfLines={1}
            >
              {titre}
            </Text>
            {edition && (
              <Tooltip label={t('client.conversations.modifier_titre')}>
                <IconButton
                  aria-label={t('client.conversations.modifier_titre')}
                  icon={<EditIcon />}
                  variant='ghost'
                  color='gray.dark'
                  size='xs'
                  onClick={() => {
                    edition.onStart(titre);
                  }}
                />
              </Tooltip>
            )}
          </>
        )}
      </HStack>
      <IconButton
        aria-label={t('global.fermer')}
        icon={<ChevronDownIcon boxSize={6} />}
        variant='ghost'
        color='gray.dark'
        size='sm'
        onClick={onFermer}
      />
    </HStack>
  );
};
